import { differenceInSeconds, endOfDay } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import {
  any,
  aperture,
  equals,
  flatten,
  groupWith,
  head,
  last,
  pipe,
  reverse,
} from 'ramda';
import type { CMSWeekday, EWeekday } from '@/api/cms/base';
import { weekdays } from './cms';
import { timestamp } from './dateTime';

const getTimeLeftTillEndOfTodayUTC = () => {
  const now = toZonedTime(new Date(), 'UTC');
  const endOfDayUTC = endOfDay(now);
  return differenceInSeconds(endOfDayUTC, now);
};

const checkNumbersConsistent = (subset: number[], range: number[]) =>
  pipe(
    () => aperture(subset.length, range),
    (result) =>
      result.length > 0 ? any<number[]>(equals(subset))(result) : false
  )();

export const getTimeLeftFromScheduler = (scheduler?: CMSWeekday[]) => {
  if (!scheduler) return null;

  const now = toZonedTime(new Date(), 'UTC');
  const currentWeekday = format(now, 'eeee', { timeZone: 'UTC' });
  const currentWeekdayIndex = weekdays.indexOf(currentWeekday as EWeekday);

  const schedulerIndexes = scheduler
    .map(({ weekday }) => weekdays.indexOf(weekday))
    .sort();

  if (schedulerIndexes.length === 1) {
    return getTimeLeftTillEndOfTodayUTC();
  }
  const weekdaysIndexes = weekdays.map((__, idx) => idx);
  // check if sorted scheduler starts from Sunday and ends with Saturday
  // it's needed to understand where is the start of period
  if (
    head(schedulerIndexes) === head(weekdaysIndexes) &&
    last(schedulerIndexes) === last(weekdaysIndexes)
  ) {
    const consecutiveDayIndexesGroups = groupWith(
      (a, b) => a + 1 === b,
      schedulerIndexes
    );
    // if there are more than 2 groups it means that days don't follow each other
    if (consecutiveDayIndexesGroups.length !== 2) {
      return getTimeLeftTillEndOfTodayUTC();
    }
    // there are two groups - one starts from Sunday and second ends on Saturday
    // change their order and find the start day of period
    const daysPeriod = pipe(reverse, flatten)(consecutiveDayIndexesGroups);

    return last(daysPeriod) === currentWeekdayIndex
      ? getTimeLeftTillEndOfTodayUTC()
      : null;
  }
  // check if all numbers (days) follow each other
  if (checkNumbersConsistent(schedulerIndexes, weekdaysIndexes)) {
    return last(schedulerIndexes) === currentWeekdayIndex
      ? getTimeLeftTillEndOfTodayUTC()
      : null;
  }
  return getTimeLeftTillEndOfTodayUTC();
};

export const getTimeLeft = (timeUntil: string | null) =>
  timeUntil !== null ? (timestamp(timeUntil) - new Date().getTime()) / 1000 : 0;
